import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Trash2,
  UploadCloud,
} from "lucide-react";
import Layout from "../../components/layout/Layout";
import MainHeading from "../../components/common/MainHeading";
import { Button } from "../../components/ui/button";
import { showToastNotification } from "../../utility/helper";
import { RoutesLink } from "../../utility/RouteLinks";
import { ThrowError } from "../../api/callApi"; // adjust path to match your project
import { WorkerService } from "@/services/Labor/worker";
import { SiteService } from "@/services/Site/Site";
import { ShiftService } from "@/services/Shift/Shift"; // adjust path to match your project

// Minimal shape we need from the site list response.
interface ISiteOption {
  id: string;
  name: string;
}

// Minimal shape we need from the shift list response.
interface IShiftOption {
  id: string;
  name: string;
}

// ─── Row shape mirrors the real bulk-upload template columns ───────────
interface IBulkLaborRow {
  rowNumber: number;
  siteId?: string;
  workerId: string;
  fullName: string;
  fatherName: string;
  phoneNumber: string;
  aadharNumber: string;
  shiftId: string;
  checkInBufferTime: string;
  checkOutBufferTime: string;
  dailyRate: string;
  joinDate: string;
  status: string;
  contactPerson: string;
  cpNumber: string;
  gender: string;
  fieldErrors: FieldErrors;
}

type EditableField = Exclude<keyof IBulkLaborRow, "rowNumber" | "fieldErrors">;
type FieldErrors = Partial<Record<EditableField, string>>;

// Order here = order written back to the uploaded .xlsx, and must match
// the server's template headers exactly.
const TEMPLATE_COLUMNS: EditableField[] = [
  "siteId",
  "workerId",
  "fullName",
  "fatherName",
  "phoneNumber",
  "aadharNumber",
  "shiftId",
  "checkInBufferTime",
  "checkOutBufferTime",
  "dailyRate",
  "joinDate",
  "status",
  "contactPerson",
  "cpNumber",
  "gender",
];

const TEMPLATE_SAMPLE_ROW: Record<EditableField, string> = {
  siteId: "",
  workerId: "W0002",
  fullName: "Suresh Patel",
  fatherName: "Ramesh Patel",
  phoneNumber: "6464324323",
  aadharNumber: "123456789012",
  shiftId: "",
  checkInBufferTime: "00:15:00",
  checkOutBufferTime: "00:15:00",
  dailyRate: "500",
  joinDate: "2026-08-25",
  status: "active",
  contactPerson: "Mukesh",
  cpNumber: "8888888888",
  gender: "male",
};

// Column shown in the review table, each with how it should be edited.
// "siteId" and "shiftId" are handled separately below via dedicated
// dynamic-options columns, since their options come from API calls, not
// a static list.
const EDITABLE_FIELDS: {
  key: EditableField;
  label: string;
  type: "text" | "number" | "select";
  options?: string[];
}[] = [
  { key: "workerId", label: "Worker ID", type: "text" },
  { key: "fullName", label: "Full Name", type: "text" },
  { key: "fatherName", label: "Father Name", type: "text" },
  { key: "phoneNumber", label: "Phone", type: "text" },
  { key: "aadharNumber", label: "Aadhar No.", type: "text" },
  { key: "checkInBufferTime", label: "Check-In Buffer", type: "text" },
  { key: "checkOutBufferTime", label: "Check-Out Buffer", type: "text" },
  { key: "dailyRate", label: "Daily Rate", type: "number" },
  { key: "joinDate", label: "Join Date", type: "text" },
  { key: "status", label: "Status", type: "select", options: ["active", "inactive"] },
  { key: "contactPerson", label: "Contact Person", type: "text" },
  { key: "cpNumber", label: "CP Number", type: "text" },
  { key: "gender", label: "Gender", type: "select", options: ["male", "female", "other"] },
];

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls", ".csv"];

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}:\d{2}$/;

// fullName, phoneNumber, siteId and shiftId are required — everything
// else is validated for format only, if present. siteId/shiftId are
// required because the Excel template can't reasonably ask users to type
// internal IDs, so they're always picked from dropdowns in the review
// table.
const validateFields = (row: Omit<IBulkLaborRow, "rowNumber" | "fieldErrors">): FieldErrors => {
  const errors: FieldErrors = {};

  if (!(row.siteId ?? "").trim()) errors.siteId = "Select a site";
  if (!(row.shiftId ?? "").trim()) errors.shiftId = "Select a shift";

  if (!row.fullName.trim()) errors.fullName = "Required";
  if (!row.phoneNumber.trim()) errors.phoneNumber = "Required";
  else if (!/^\d{10}$/.test(row.phoneNumber.replace(/\s+/g, "")))
    errors.phoneNumber = "Must be 10 digits";

  if (row.aadharNumber && !/^\d{12}$/.test(row.aadharNumber.replace(/\s+/g, "")))
    errors.aadharNumber = "Must be 12 digits";

  if (row.dailyRate && Number.isNaN(Number(row.dailyRate)))
    errors.dailyRate = "Must be a number";

  if (row.status && !["active", "inactive"].includes(row.status.toLowerCase()))
    errors.status = "active/inactive only";

  if (row.gender && !["male", "female", "other"].includes(row.gender.toLowerCase()))
    errors.gender = "male/female/other only";

  if (row.joinDate && !DATE_RE.test(row.joinDate))
    errors.joinDate = "Use YYYY-MM-DD";

  if (row.checkInBufferTime && !TIME_RE.test(row.checkInBufferTime))
    errors.checkInBufferTime = "Use HH:mm:ss";

  if (row.checkOutBufferTime && !TIME_RE.test(row.checkOutBufferTime))
    errors.checkOutBufferTime = "Use HH:mm:ss";

  return errors;
};

// Strips a trailing "*" (and any surrounding whitespace) before comparing
// header names — the real template marks required columns as "fullName*",
// "phoneNumber*", etc., which an exact match would otherwise miss.
const normalizeHeader = (s: string) => s.trim().toLowerCase().replace(/[*\s]+$/g, "");

const parseRow = (raw: Record<string, unknown>, rowNumber: number): IBulkLaborRow => {
  const get = (key: EditableField) => {
    const found = Object.keys(raw).find((k) => normalizeHeader(k) === normalizeHeader(key));
    const value = found ? raw[found] : undefined;
    return value === undefined || value === null ? "" : String(value).trim();
  };

  const base = {
    siteId: get("siteId"),
    workerId: get("workerId"),
    fullName: get("fullName"),
    fatherName: get("fatherName"),
    phoneNumber: get("phoneNumber"),
    aadharNumber: get("aadharNumber"),
    shiftId: get("shiftId"),
    checkInBufferTime: get("checkInBufferTime"),
    checkOutBufferTime: get("checkOutBufferTime"),
    dailyRate: get("dailyRate"),
    joinDate: get("joinDate"),
    status: get("status").toLowerCase() || "active",
    contactPerson: get("contactPerson"),
    cpNumber: get("cpNumber"),
    gender: get("gender").toLowerCase(),
  };

  return { rowNumber, ...base, fieldErrors: validateFields(base) };
};

const BulkAddLabor: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<IBulkLaborRow[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [editingCell, setEditingCell] = useState<{ row: number; field: EditableField } | null>(null);

  // ─── Site dropdown options, fetched once on mount ─────────────────
  const [siteOptions, setSiteOptions] = useState<ISiteOption[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        // getSiteList is paginated — request a large page so every site
        // shows up in the dropdown instead of just the first page's worth.
        const res = await SiteService.getSiteList({ pageIndex: 1, pageSize: 500 });
        const rows = res?.data?.rows ?? [];
        const options: ISiteOption[] = rows.map((s) => ({
          id: s.id,
          name: s.siteName,
        }));
        setSiteOptions(options);
      } catch (err) {
        showToastNotification("danger", "Could not load site list");
      } finally {
        setIsLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  // ─── Shift dropdown options, fetched once on mount ─────────────────
  const [shiftOptions, setShiftOptions] = useState<IShiftOption[]>([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);

  useEffect(() => {
    const fetchShifts = async () => {
      setIsLoadingShifts(true);
      try {
        // getShiftList is paginated — request a large page so every shift
        // shows up in the dropdown instead of just the first page's worth.
        const res = await ShiftService.getShiftList({ pageIndex: 1, pageSize: 500 });
        const rows = res?.data?.rows ?? [];
        const options: IShiftOption[] = rows.map((s) => ({
          id: s.id,
          name: s.shiftName,
        }));
        setShiftOptions(options);
      } catch (err) {
        showToastNotification("danger", "Could not load shift list");
      } finally {
        setIsLoadingShifts(false);
      }
    };
    fetchShifts();
  }, []);

  const validCount = rows.filter((r) => Object.keys(r.fieldErrors).length === 0).length;
  const invalidCount = rows.length - validCount;

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await WorkerService.downloadBulkTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "labor-bulk-add-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showToastNotification("danger", "Failed to download template. Generating a local copy instead.");
      const ws = XLSX.utils.json_to_sheet([TEMPLATE_SAMPLE_ROW], { header: TEMPLATE_COLUMNS });
      ws["!cols"] = TEMPLATE_COLUMNS.map(() => ({ wch: 20 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Workers");
      XLSX.writeFile(wb, "labor-bulk-add-template.xlsx");
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const parseFile = useCallback((incoming: File) => {
    const ext = `.${incoming.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      showToastNotification("danger", "Please upload a .xlsx, .xls or .csv file");
      return;
    }

    setIsParsing(true);
    setFile(incoming);
    setRows([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet, { defval: "" });

        if (!json.length) {
          showToastNotification("danger", "No rows found in this file");
          setIsParsing(false);
          return;
        }

        setRows(json.map((raw, idx) => parseRow(raw, idx + 2)));
      } catch (err) {
        showToastNotification("danger", "Could not read this file. Please check the format.");
      } finally {
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      showToastNotification("danger", "Failed to read the file");
      setIsParsing(false);
    };
    reader.readAsBinaryString(incoming);
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) parseFile(selected);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) parseFile(dropped);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setRows([]);
    setEditingCell(null);
  };

  // Editing a cell re-validates just that row so the "Issues" column and
  // ready/needs-fixing counters update immediately.
  const handleCellChange = (rowIndex: number, field: EditableField, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      const updatedRow = { ...next[rowIndex], [field]: value };
      updatedRow.fieldErrors = validateFields(updatedRow);
      next[rowIndex] = updatedRow;
      return next;
    });
  };

  // Rebuilds an .xlsx from the (possibly edited) rows in memory — this is
  // what actually gets uploaded, since the server only accepts a file.
  const buildEditedFile = (originalName: string): File => {
    const data = rows.map((r) =>
      TEMPLATE_COLUMNS.reduce((acc, key) => {
        acc[key] = r[key] ?? "";
        return acc;
      }, {} as Record<EditableField, string>),
    );
    const ws = XLSX.utils.json_to_sheet(data, { header: TEMPLATE_COLUMNS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Workers");
    const arrayBuffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    return new File([blob], originalName, { type: blob.type });
  };

  const handleSubmit = async () => {
    if (!file || !rows.length || invalidCount > 0) return;
    setIsSubmitting(true);
    try {
      const uploadFile = buildEditedFile(file.name);
      const response = await WorkerService.bulkUploadWorkers(uploadFile);
      if (response?.success) {
        showToastNotification(
          "success",
          response?.message || `${rows.length} labor record(s) added successfully`,
        );
        navigate(RoutesLink.labor);
      } else {
        showToastNotification("danger", response?.message || "Failed to upload labor records");
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="h-9 w-9 p-0 hover:bg-muted"
            onClick={() => navigate(RoutesLink.labor)}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <MainHeading title="Bulk Add Labor" />
        </div>

        <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Use the template to add labor in bulk</p>
              <p className="text-sm text-gray-500 mt-0.5">
                Download the template, fill in worker details, then upload it below. Full Name, Phone, Site and Shift are required for every row — you can also fix flagged rows directly in the table below.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="gap-2 shrink-0"
            disabled={isDownloadingTemplate}
          >
            {isDownloadingTemplate ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download size={16} />
            )}
            Download Template
          </Button>
        </div>

        {!file && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`bg-white border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
              isDragging ? "border-primary bg-primary/5" : "border-slate-200 hover:border-primary/40 hover:bg-slate-50"
            }`}
          >
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <UploadCloud className="h-7 w-7 text-primary" />
            </div>
            <p className="text-sm font-semibold text-gray-800">
              Drag and drop your file here, or <span className="text-primary">browse</span>
            </p>
            <p className="text-xs text-gray-400 mt-1.5">Supports .xlsx, .xls and .csv</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFileInput}
            />
          </div>
        )}

        {file && (
          <div className="bg-white border border-slate-200/70 rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{file.name}</p>
                  <p className="text-xs text-gray-400">{formatBytes(file.size)}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                onClick={handleRemoveFile}
                className="h-9 w-9 p-0 text-red-500 hover:bg-red-50 hover:text-red-600 shrink-0"
                disabled={isSubmitting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {isParsing && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-5">
                <Loader2 className="h-4 w-4 animate-spin" /> Reading file...
              </div>
            )}

            {!isParsing && rows.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2.5 mt-5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {rows.length} row{rows.length === 1 ? "" : "s"} found
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-green-50 text-green-600 border border-green-200">
                    <CheckCircle2 size={13} /> {validCount} ready
                  </span>
                  {invalidCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-red-50 text-red-600 border border-red-200">
                      <AlertCircle size={13} /> {invalidCount} need fixing
                    </span>
                  )}
                  {isLoadingSites && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading sites...
                    </span>
                  )}
                  {isLoadingShifts && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading shifts...
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">Click any cell to edit it</span>
                </div>

                <div className="mt-4 border border-slate-200 rounded-lg overflow-x-auto max-h-[480px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-2.5 whitespace-nowrap">Row</th>
                        <th className="px-3 py-2.5 whitespace-nowrap">Site</th>
                        <th className="px-3 py-2.5 whitespace-nowrap">Shift</th>
                        {EDITABLE_FIELDS.map((f) => (
                          <th key={f.key} className="px-3 py-2.5 whitespace-nowrap">
                            {f.label}
                          </th>
                        ))}
                        <th className="px-3 py-2.5 whitespace-nowrap">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {rows.map((row, rowIndex) => {
                        const errorList = Object.values(row.fieldErrors);
                        const isEditingSite =
                          editingCell?.row === rowIndex && editingCell?.field === "siteId";
                        const selectedSite = siteOptions.find((s) => s.id === row.siteId);
                        const isEditingShift =
                          editingCell?.row === rowIndex && editingCell?.field === "shiftId";
                        const selectedShift = shiftOptions.find((s) => s.id === row.shiftId);

                        return (
                          <tr key={row.rowNumber} className={errorList.length ? "bg-red-50/40" : ""}>
                            <td className="px-3 py-2 text-gray-400">{row.rowNumber}</td>

                            {/* Site — always a dropdown, independent of Shift */}
                            <td className="px-1.5 py-1">
                              {isEditingSite ? (
                                <select
                                  autoFocus
                                  value={row.siteId}
                                  onChange={(e) => handleCellChange(rowIndex, "siteId", e.target.value)}
                                  onBlur={() => setEditingCell(null)}
                                  disabled={isLoadingSites}
                                  className="w-full min-w-[140px] px-2 py-1 text-sm border border-primary rounded-md focus:outline-none"
                                >
                                  <option value="">Select site</option>
                                  {siteOptions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  onClick={() => setEditingCell({ row: rowIndex, field: "siteId" })}
                                  className={`inline-block w-full min-w-[140px] px-2 py-1 whitespace-nowrap cursor-pointer hover:bg-slate-50 rounded ${
                                    row.fieldErrors.siteId
                                      ? "text-red-600 ring-1 ring-inset ring-red-300 rounded-md"
                                      : "text-gray-700"
                                  }`}
                                  title={row.fieldErrors.siteId || "Click to select"}
                                >
                                  {selectedSite?.name || <span className="text-gray-300">Select site</span>}
                                </span>
                              )}
                            </td>

                            {/* Shift — always a dropdown, same pattern as Site */}
                            <td className="px-1.5 py-1">
                              {isEditingShift ? (
                                <select
                                  autoFocus
                                  value={row.shiftId}
                                  onChange={(e) => handleCellChange(rowIndex, "shiftId", e.target.value)}
                                  onBlur={() => setEditingCell(null)}
                                  disabled={isLoadingShifts}
                                  className="w-full min-w-[140px] px-2 py-1 text-sm border border-primary rounded-md focus:outline-none"
                                >
                                  <option value="">Select shift</option>
                                  {shiftOptions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                      {s.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span
                                  onClick={() => setEditingCell({ row: rowIndex, field: "shiftId" })}
                                  className={`inline-block w-full min-w-[140px] px-2 py-1 whitespace-nowrap cursor-pointer hover:bg-slate-50 rounded ${
                                    row.fieldErrors.shiftId
                                      ? "text-red-600 ring-1 ring-inset ring-red-300 rounded-md"
                                      : "text-gray-700"
                                  }`}
                                  title={row.fieldErrors.shiftId || "Click to select"}
                                >
                                  {selectedShift?.name || <span className="text-gray-300">Select shift</span>}
                                </span>
                              )}
                            </td>

                            {EDITABLE_FIELDS.map((f) => {
                              const hasError = Boolean(row.fieldErrors[f.key]);
                              const isEditing =
                                editingCell?.row === rowIndex && editingCell?.field === f.key;
                              const value = row[f.key];

                              if (isEditing) {
                                if (f.type === "select") {
                                  return (
                                    <td key={f.key} className="px-1.5 py-1">
                                      <select
                                        autoFocus
                                        value={value}
                                        onChange={(e) => handleCellChange(rowIndex, f.key, e.target.value)}
                                        onBlur={() => setEditingCell(null)}
                                        className="w-full min-w-[100px] px-2 py-1 text-sm border border-primary rounded-md focus:outline-none"
                                      >
                                        <option value="">—</option>
                                        {f.options?.map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    </td>
                                  );
                                }
                                return (
                                  <td key={f.key} className="px-1.5 py-1">
                                    <input
                                      autoFocus
                                      type={f.type === "number" ? "number" : "text"}
                                      value={value}
                                      onChange={(e) => handleCellChange(rowIndex, f.key, e.target.value)}
                                      onBlur={() => setEditingCell(null)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === "Escape") setEditingCell(null);
                                      }}
                                      className="w-full min-w-[110px] px-2 py-1 text-sm border border-primary rounded-md focus:outline-none"
                                    />
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={f.key}
                                  onClick={() => setEditingCell({ row: rowIndex, field: f.key })}
                                  className={`px-3 py-2 whitespace-nowrap cursor-pointer hover:bg-slate-50 rounded ${
                                    hasError ? "text-red-600 ring-1 ring-inset ring-red-300 rounded-md" : "text-gray-700"
                                  }`}
                                  title={row.fieldErrors[f.key] || "Click to edit"}
                                >
                                  {value || <span className="text-gray-300">—</span>}
                                </td>
                              );
                            })}
                            <td className="px-3 py-2">
                              {errorList.length ? (
                                <span className="text-xs text-red-600">{errorList.join(", ")}</span>
                              ) : (
                                <span className="text-xs text-green-600">Looks good</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => navigate(RoutesLink.labor)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isParsing || !rows.length || invalidCount > 0 || isSubmitting}
                className="gap-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSubmitting
                  ? "Uploading..."
                  : invalidCount > 0
                  ? `Fix ${invalidCount} row${invalidCount === 1 ? "" : "s"} to continue`
                  : `Add ${rows.length || ""} Labor Record${rows.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
      </div>
  );
};

export default BulkAddLabor;